class BannerSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.image-banner-container-wrapper');
    this.prevButton = this.querySelector('#prevButton_imageBanner');
    this.nextButton = this.querySelector('#nextButton_imageBanner');
    this.dotsContainer = this.querySelector('.banner_slider__dots');
    this.originalSlides = []; // Store original slides for reinitialization
  }

  connectedCallback() {
    if (!this.slider) {
      console.error('Slider element not found.');
      return;
    }

    // Store original slides for reinitialization
    this.originalSlides = Array.from(this.slider.children);

    // Initial setup
    this.initializeSlider();

    // Add event listeners for navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollSlider(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollSlider(1));
    }

    // Add event listeners for slider scroll
    this.slider.addEventListener('scroll', () => {
      this.updateActiveDot();
      this.handleButtonState();
    });

    // Reinitialize the slider on window resize
    window.addEventListener('resize', () => {
      this.initializeSlider();
    });
  }

  initializeSlider() {
    // Clear existing slides and dots
    this.slider.innerHTML = '';
    this.dotsContainer.innerHTML = '';

    // Restore original slides
    this.originalSlides.forEach((slide) => {
      this.slider.appendChild(slide);
    });

    // Apply filtering and recreate dots
    this.filterSlidesForCurrentScreen();

    // Check if there are any slides left
    const remainingSlides = this.slider.querySelectorAll('.image-banner-container');

    if (remainingSlides.length <= 1) {
      // Hide dots and buttons if one or no slides are left
      this.hideSliderControls();
    } else {
      // Show dots and buttons if slides are present
      this.showSliderControls();

      // Recreate dots and update slider state
      this.createDots();
      this.updateActiveDot();
      this.handleButtonState();
    }
  }

  filterSlidesForCurrentScreen() {
    const isMobile = window.innerWidth <= 768;

    const slides = this.slider.querySelectorAll('.image-banner-container');
    slides.forEach((slide) => {
      const selector1 = slide.getAttribute('data-selector-1');
      const selector2 = slide.getAttribute('data-selector-2');

      // Remove slides based on screen size
      if (isMobile && selector1 === 'video' && selector2 != '' ) {
        slide.remove();
      }
    });
  }

  createDots() {
    const slides = this.slider.querySelectorAll('.image-banner-container');

    slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.classList.add('slider-dot');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.addEventListener('click', () => this.scrollToSlide(index));
      this.dotsContainer.appendChild(dot);
    });
  }

  updateActiveDot() {
    const slides = this.slider.querySelectorAll('.image-banner-container');
    if (slides.length === 0) return;

    const slideWidth = slides[0].offsetWidth;
    const scrollLeft = this.slider.scrollLeft;
    const currentSlide = Math.round(scrollLeft / slideWidth);

    const dots = this.dotsContainer.querySelectorAll('.slider-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === currentSlide);
    });
  }

  handleButtonState() {
    const slides = this.slider.querySelectorAll('.image-banner-container');
    if (slides.length <= 1) {
      // Hide buttons if one or no slides are left
      this.hideSliderControls();
      return;
    }

    const scrollLeft = this.slider.scrollLeft;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;

    const buffer = 100;

    // Handle prevButton state
    if (scrollLeft <= 0) {
      this.prevButton.setAttribute('disabled', true);

      // Change SVG path fill for prevButton (mobile only)
      if (window.innerWidth <= 768) {
        const prevButtonSpan = this.prevButton.querySelector('span');
        if (prevButtonSpan) {
          const svg = prevButtonSpan.querySelector('svg');
          if (svg) {
            const path = svg.querySelector('path');
            if (path) {
              path.setAttribute('fill', 'grey'); // Change the fill color to grey
            }
          }
        }
      }
    } else {
      this.prevButton.removeAttribute('disabled');

      // Reset SVG path fill for prevButton (mobile only)
      if (window.innerWidth <= 768) {
        const prevButtonSpan = this.prevButton.querySelector('span');
        if (prevButtonSpan) {
          const svg = prevButtonSpan.querySelector('svg');
          if (svg) {
            const path = svg.querySelector('path');
            if (path) {
              path.setAttribute('fill', 'black'); // Reset the fill color
            }
          }
        }
      }
    }

    // Handle nextButton state
    if (scrollLeft >= maxScroll - buffer) {
      this.nextButton.setAttribute('disabled', true);

      // Change SVG path fill for nextButton (mobile only)
      if (window.innerWidth <= 768) {
        const nextButtonSpan = this.nextButton.querySelector('span');
        if (nextButtonSpan) {
          const svg = nextButtonSpan.querySelector('svg');
          if (svg) {
            const path = svg.querySelector('path');
            if (path) {
              path.setAttribute('fill', 'grey'); // Change the fill color to grey
            }
          }
        }
      }
    } else {
      this.nextButton.removeAttribute('disabled');

      // Reset SVG path fill for nextButton (mobile only)
      if (window.innerWidth <= 768) {
        const nextButtonSpan = this.nextButton.querySelector('span');
        if (nextButtonSpan) {
          const svg = nextButtonSpan.querySelector('svg');
          if (svg) {
            const path = svg.querySelector('path');
            if (path) {
              path.setAttribute('fill', 'black'); // Reset the fill color
            }
          }
        }
      }
    }
  }

  scrollSlider(direction) {
    const slides = this.slider.querySelectorAll('.image-banner-container');
    if (slides.length === 0) return;

    const slideWidth = slides[0].offsetWidth;
    this.slider.scrollBy({ left: direction * slideWidth, behavior: 'smooth' });
  }

  scrollToSlide(index) {
    const slides = this.slider.querySelectorAll('.image-banner-container');
    if (slides.length === 0) return;

    const slideWidth = slides[0].offsetWidth;
    this.slider.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
  }

  hideSliderControls() {
    // Hide dots and buttons
    this.dotsContainer.style.display = 'none';
    if (this.prevButton) this.prevButton.style.display = 'none';
    if (this.nextButton) this.nextButton.style.display = 'none';
  }

  showSliderControls() {
    // Show dots and buttons
    this.dotsContainer.style.display = '';
    if (this.prevButton) this.prevButton.style.display = '';
    if (this.nextButton) this.nextButton.style.display = '';
  }
}

// Register the custom element
customElements.define('banner-slider-component', BannerSliderComponent);